import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMyMaterials } from "../hooks/useMyMaterials";
import { useAuth } from "../hooks/useAuth";
import { useKeywords } from "../hooks/useKeywords";
import { useNotifications } from "../hooks/useNotifications";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import MaterialCard from "../Components/MaterialCard/MaterialCard";
import Footer from "../Components/Footer/Footer";
import "./MeusMateriaisPage.css";

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailUrl?: string;
}

const MeusMateriaisPage: React.FC = () => {
  const { materials, isLoading, error, fetchMyMaterials } = useMyMaterials();
  const { keywords, isLoading: keywordsLoading, fetchKeywords } = useKeywords();
  const [selectedCategory, setSelectedCategory] = useState<string>("todos");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const { isAuthenticated, logout: authLogout } = useAuth();
  const { hasRecentNotifications, fetchNotifications } = useNotifications();
  const navigate = useNavigate();

  // Buscar materiais e notificações quando o componente carrega
  useEffect(() => {
    fetchMyMaterials();
    fetchKeywords();
    fetchNotifications();
  }, [fetchMyMaterials, fetchKeywords, fetchNotifications]);

  // Construir categorias: "todos" + keywords da API
  const categories = ["todos", ...keywords];

  const filteredMaterials = (Array.isArray(materials) ? materials : []).filter(
    (material) => {
      const matchesCategory =
        selectedCategory === "todos" ||
        material.keywords.includes(selectedCategory);
      const matchesSearch =
        material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    }
  );

  const handleMaterialClick = (material: Material) => {
    navigate(`/material/${material.id}`);
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando meus materiais...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Ops! Algo deu errado</h2>
        <p>{error}</p>
        <button onClick={fetchMyMaterials} className="retry-button">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="vitrine-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton 
                isOpen={isMenuOpen} 
                onClick={toggleMenu}
                hasNotification={hasRecentNotifications()}
              />
            )}
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu 
          isOpen={isMenuOpen} 
          onClose={closeMenu}
          hasRecentNotifications={hasRecentNotifications()}
        />
      )}

      <main className="page-main">
        <div className="page-title">
          <h1>Meus Materiais</h1>
          <p>Seus materiais adquiridos e em progresso</p>
        </div>

        <div className="filters-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Buscar meus materiais..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="category-filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-filter ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "todos" ? "Todos" : category}
              </button>
            ))}
          </div>
        </div>

        <div className="materials-grid">
          {filteredMaterials.length > 0 ? (
            filteredMaterials.map((material) => (
              <MaterialCard
                key={material.id}
                id={material.id}
                title={material.title}
                description={material.description}
                thumbnailUrl={material.thumbnailUrl}
                onClick={() => handleMaterialClick(material)}
              />
            ))
          ) : (
            <div className="no-results">
              <h3>Nenhum material encontrado</h3>
              <p>Tente ajustar os filtros ou termo de busca</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
};

export default MeusMateriaisPage;
